import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { Farm } from '../models/farm.model';
import { Page } from '../models/page.model';
import { Parcel } from '../models/parcel.model';
import { ARBleService } from './agrorendement-ble.service';
import { ARColzaService } from './agrorendement-colza.service';
import { AROrgeService } from './agrorendement-orge.service';

export interface PlanningFilterExploitation {
  prefixExploitation: string;
  prefixConseiller: string;
  postalCode: string;
  organisationId: number | string;
}

export interface PlanningFilterParcel {
  prefixParcel: string;
  prefixIdParcel: number;
  dateAvant: string;
  dateApres: string;
  postalCode: string;
  prefixExploitation: string;
  prefixAdvisor: string;
  datePlanningAfter: string;
  datePlanningBefore: string;
  specy: number | string;
  variety: number | string;
  organisationId: number | string;
  personalizedDate: number | string;
  weekNumber?: number;
  date?: string;
  defaultWeekIdSet?: boolean;
  suspended: boolean;
}
@Injectable({ providedIn: 'root' })
export class PlanningService {
  protected baseUrl = '/tools/agrorendement/results/genconsole/{culture}';

  protected toolUrl = '/tools/agrorendement/{culture}';

  private farmsUrl = '/farms';

  constructor(
    private http: HttpClient,
    private colzaService: ARColzaService,
    private bleService: ARBleService,
    private orgeService: AROrgeService,
  ) {}

  getCultureUrl(culture: string) {
    return this.baseUrl.replace('{culture}', culture);
  }
  getCultureUrlBis(culture: string) {
    return this.toolUrl.replace('{culture}', culture);
  }

  getFarmsAdvanced(
    culture: string,
    pageIndex: number,
    itemsPerPage: number,
    sortColumnName: string,
    sortColumnOrder: string,
    filter: string,
  ): Observable<Page<Farm>> {
    const gcFarmsUrl = this.getCultureUrl(culture) + this.farmsUrl;
    let url = gcFarmsUrl + '?size=' + itemsPerPage + '&page=' + (pageIndex - 1);
    if (filter) {
      url += '&prefix=' + encodeURIComponent(filter.trim());
    }
    if (sortColumnName) {
      url += '&sort=' + sortColumnName + ',' + sortColumnOrder;
    }
    return this.http.get<any>(url).pipe(
      map((response) => response.data),
      catchError(this.handleError),
    );
  }

  getParcelsForFarms(culture: string, passage: string, farmIds: number[]): Observable<Parcel[]> {
    const tmpUrl = this.getCultureUrlBis(culture);
    if (farmIds) {
      const url = tmpUrl + '/parcels?passage=' + passage + '&farm=' + farmIds.join(',');

      return this.http.get<any>(url).pipe(
        map((response) => {
          const data = response.data;
          return data.content;
        }),
        catchError(this.handleError),
      );
    }
    return null;
  }

  getParcelsFarms(
    culture: string,
    passage: string,
    campaignId: number,
    farmIds: number[],
    sortColumnName: string,
    sortColumnOrder: string,
    filterExploitation: PlanningFilterExploitation,
    filterParcel: PlanningFilterParcel,
    onlyId: boolean,
    page: number = 0,
    size: number = 10,
  ): Observable<any> {
    return this.getParcelsFarmsInternal(
      culture,
      passage,
      campaignId,
      farmIds,
      sortColumnName,
      sortColumnOrder,
      filterExploitation,
      filterParcel,
      onlyId,
      page,
      size,
    );
  }

  getAllParcelsFarms(
    culture: string,
    passage: string,
    campaignId: number,
    farmIds: number[],
    sortColumnName: string,
    sortColumnOrder: string,
    filterExploitation: PlanningFilterExploitation,
    filterParcel: PlanningFilterParcel,
    onlyId: boolean,
  ): Observable<any> {
    return this.getParcelsFarmsInternal(
      culture,
      passage,
      campaignId,
      farmIds,
      sortColumnName,
      sortColumnOrder,
      filterExploitation,
      filterParcel,
      onlyId,
      undefined,
      undefined,
      true,
    );
  }

  private getParcelsFarmsInternal(
    culture: string,
    passage: string,
    campaignId: number,
    farmIds: number[],
    sortColumnName: string,
    sortColumnOrder: string,
    filterExploitation: PlanningFilterExploitation,
    filterParcel: PlanningFilterParcel,
    onlyId: boolean,
    page?: number,
    size?: number,
    isAll: boolean = false,
  ): Observable<any> {
    const tmpUrl = this.getCultureUrlBis(culture);
    let url = isAll ? `${tmpUrl}/parcels/all?passage=${encodeURIComponent(passage)}` : `${tmpUrl}/parcels?passage=${encodeURIComponent(passage)}`;

    if (!isAll && page !== undefined && size !== undefined) {
      url += `&page=${page}&size=${size}`;
    }
    if (onlyId != null) {
      url += `&onlyIds=${onlyId}`;
    }
    if (filterExploitation) {
      if (filterExploitation.prefixExploitation) {
        url += `&prefixExploitation=${encodeURIComponent(filterExploitation.prefixExploitation.trim())}`;
      }
      if (filterExploitation.postalCode) {
        url += `&postalCodeExp=${encodeURIComponent(filterExploitation.postalCode.trim())}`;
      }
      if (filterExploitation.organisationId) {
        url += `&organisation=${filterExploitation.organisationId}`;
      }
      if (filterExploitation.prefixConseiller) {
        url += `&advisor=${encodeURIComponent(filterExploitation.prefixConseiller.trim())}`;
      }
    }
    if (sortColumnName) {
      url += `&sort=${sortColumnName},${sortColumnOrder}`;
    }
    if (farmIds) {
      url += `&farm=${farmIds.join(',')}`;
    }
    if (filterParcel) {
      if (filterParcel.prefixParcel) {
        url += `&prefixParcel=${encodeURIComponent(filterParcel.prefixParcel.trim())}`;
      }
      if (filterParcel.prefixIdParcel) {
        url += `&prefixIdParcel=${filterParcel.prefixIdParcel}`;
      }
      if (filterParcel.postalCode) {
        url += `&postalCodePar=${encodeURIComponent(filterParcel.postalCode.trim())}`;
      }
      if (filterParcel.prefixExploitation) {
        url += `&prefixExploitation=${encodeURIComponent(filterParcel.prefixExploitation.trim())}`;
      }
      if (filterParcel.prefixAdvisor) {
        url += `&advisor=${encodeURIComponent(filterParcel.prefixAdvisor.trim())}`;
      }
      if (filterParcel.dateAvant) {
        url += `&dateAvant=${filterParcel.dateAvant}`;
      }
      if (filterParcel.dateApres) {
        url += `&dateApres=${filterParcel.dateApres}`;
      }
      if (filterParcel.datePlanningAfter) {
        url += `&datePlanAfter=${filterParcel.datePlanningAfter}`;
      }
      if (filterParcel.datePlanningBefore) {
        url += `&datePlanBefore=${filterParcel.datePlanningBefore}`;
      }
      if (filterParcel.specy && filterParcel.specy !== 'null') {
        url += `&specy=${encodeURIComponent(String(filterParcel.specy))}`;
      }
      if (filterParcel.variety && filterParcel.variety !== 'null') {
        url += `&variety=${encodeURIComponent(String(filterParcel.variety))}`;
      }
      if (filterParcel.organisationId) {
        url += `&organisation=${filterParcel.organisationId}`;
      }
      if (filterParcel.suspended) {
        url += `&suspended=${filterParcel.suspended}`;
      }
      if (filterParcel.personalizedDate) {
        url += `&defaultWeekIdSet=${filterParcel.personalizedDate}`;
      }
    }

    url += `&campaign=${campaignId}`;

    return this.http.get<any>(url).pipe(catchError(this.handleError));
  }

  getFarms(
    culture: string,
    passage: string,
    campaignId: number,
    farmIds: number[],
    sortColumnName: string,
    sortColumnOrder: string,
    filterExploitation: PlanningFilterExploitation,
    filterParcel: PlanningFilterParcel,
  ): Observable<Parcel[]> {
    const tmpUrl = this.getCultureUrlBis(culture);
    let url = tmpUrl + '/farms/all';
    url = url + '?passage=' + encodeURIComponent(passage);
    if (filterExploitation) {
      if (filterExploitation.prefixExploitation) {
        url += '&prefixExploitation=' + encodeURIComponent(filterExploitation.prefixExploitation.trim());
      }
      if (filterExploitation.postalCode) {
        url += '&postalCodeExp=' + encodeURIComponent(filterExploitation.postalCode.trim());
      }
      if (filterExploitation.organisationId) {
        url += '&organisation=' + filterExploitation.organisationId;
      }
      if (filterExploitation.prefixConseiller) {
        url += '&advisor=' + encodeURIComponent(filterExploitation.prefixConseiller.trim());
      }
    }
    if (sortColumnName) {
      url += '&sort=' + sortColumnName + ',' + sortColumnOrder;
    }
    if (farmIds) {
      url += '&farm=' + farmIds.join(',');
    }
    if (filterParcel) {
      if (filterParcel.postalCode) {
        url += '&postalCodePar=' + encodeURIComponent(filterParcel.postalCode.trim());
      }
    }
    //en dure, a voir comment le gérer -> faire un get "current campaign ou un truc du genre "
    url += '&campaign=' + campaignId;

    return this.http.get<any>(url).pipe(
      map((response) => {
        const data = response.data;
        return data;
      }),
      catchError(this.handleError),
    );
  }

  private handleError(error: any) {
    // FIXME
    console.error('Une erreur est survenue', error);
    return throwError(() => error.message || error);
  }

  getParcel(id: number, culture: string) {
    let parcel: any;
    if (culture == 'ble') {
      parcel = this.bleService.getParcel(id);
    } else if (culture == 'orge') {
      parcel = this.orgeService.getParcel(id);
    } else if (culture == 'colza') {
      parcel = this.colzaService.getParcel(id);
    } else {
      return null;
    }
    return parcel;
  }

  getParcelsGroupedByDateAndWeek(
    culture: string,
    farmIds: number[],
    adviceType: string,
    campaignId: number,
    prefixExploitation: string,
    organisationId: number,
    prefixAdvisor: string,
  ): Observable<any> {
    const baseUrl = this.getCultureUrlBis(culture);
    let url = `${baseUrl}/planning?`;

    if (farmIds && farmIds.length > 0) {
      url += `farmIds=${farmIds.join(',')}`;
    }

    if (adviceType) {
      url += `&adviceType=${adviceType}`;
    }

    if (campaignId) {
      url += `&campaignId=${campaignId}`;
    }

    if (prefixExploitation) {
      url += `&prefixExploitation=${encodeURIComponent(prefixExploitation.trim())}`;
    }

    if (organisationId) {
      url += `&organisation=${organisationId}`;
    }

    if (prefixAdvisor) {
      url += `&advisor=${encodeURIComponent(prefixAdvisor.trim())}`;
    }

    return this.http.get<any>(url).pipe(catchError(this.handleError));
  }
}
